/**
 * **一张牌的节点** —— 复用与手牌扇**同一个** `CardView` + `CardRig`。
 *
 * 用途：记牌器表头、A2 选牌、异常预示、观众露牌…… 所有"屏幕上的一张小牌"。
 * 它自带 idle 摇摆、hover 抬起（`CardRig` 那套）与出血裁切（`CardView.clip`），
 * 所以调用方不必再各写一套"画张图 + 自己算 hover"。
 */
import { Container, Rectangle } from 'pixi.js';
import { CardRig } from '../hand/cardRig';
import { CardView } from '../card/cardView';
import { faceSize, type FaceSize } from '../card/metrics';
import { DEFAULT_POSE } from '../card/pose';
import type { CardFaceCache } from '../card/faceCache';
import { canHoverNow } from '../input/current';
import type { CardDef } from '../../core/card';

export interface CardActorOptions {
  readonly faces: CardFaceCache;
  readonly def: CardDef;
  /** 牌高（逻辑像素）；宽度按统一比例算 */
  readonly height: number;
  /** 裁掉出血。默认 false —— 和手牌一样**保留有意设计的破框** */
  readonly clip?: boolean;
  readonly onHover?: (def: CardDef | null) => void;
  readonly onPick?: () => void;
}

export class CardActor {
  /** 加进场景的容器。原点在**左上角**，牌正好占 (0,0)..(w,h) */
  readonly view: Container;
  readonly def: CardDef;
  readonly size: FaceSize;
  readonly #rig: CardRig;
  #hovered = false;
  #picked = false;

  constructor(options: CardActorOptions) {
    this.def = options.def;
    this.size = faceSize(options.height);
    this.view = new Container();
    this.view.label = 'card-actor';

    this.#rig = new CardRig({ view: this.view });
    this.#rig.add(
      new CardView({
        texture: options.faces.get(this.def, this.size),
        size: this.size,
        clip: options.clip ?? false,
      }),
    );
    // CardView 原点在牌心 → 想让它占 (0,0)..(w,h)，牌心得在 (w/2, h/2)
    this.#rig.settle(0, { ...DEFAULT_POSE, x: this.size.width / 2, y: this.size.height / 2 });

    this.view.eventMode = 'static';
    this.view.cursor = 'pointer';
    this.view.hitArea = new Rectangle(0, 0, this.size.width, this.size.height);
    // ⚠️ 装饰性 hover 只对**真能 hover**的指针生效。触控点完一次之后浏览器
    //    还会把指针位置留在原地，照着它算 hover 会让牌反复抬起/放下。
    this.view.on('pointerover', () => {
      if (!canHoverNow()) return;
      this.#hovered = true;
      options.onHover?.(this.def);
    });
    this.view.on('pointerout', () => {
      if (!this.#hovered) return;
      this.#hovered = false;
      options.onHover?.(null);
    });
    this.view.on('pointertap', () => {
      this.#picked = true;
      options.onPick?.();
      this.#picked = false;
    });
  }

  /** 每帧推一次（idle / hover 都在 `CardRig` 里） */
  update(dtMs: number): void {
    const dt = Math.min(dtMs / 1000, 0.05);
    this.#rig.step(dt, 0, 0, [
      {
        posture: { ...DEFAULT_POSE, x: this.size.width / 2, y: this.size.height / 2 },
        input: {
          phase: 0,
          index: 0,
          hovered: this.#hovered ? 0 : -1,
          selected: this.#picked,
          pointer: null,
          unit: this.size.height,
          wide: this.size.width,
        },
        wantHover: this.#hovered ? 1 : 0,
        wantPress: this.#picked ? 1 : 0,
      },
    ]);
  }

  /** 跟音乐鼓动 */
  pulse(): void {
    this.#rig.pulse();
  }

  destroy(): void {
    this.#rig.clear();
    this.view.destroy({ children: true });
  }
}
